// src/pages/DataSourceSettings.tsx
// Settings → Data Source tab.
// Tier A (Developer / System Admin) only.
// Satisfies: Technical Secrets & Hosting Roadmap v04 Section 3.2
//
// Allows the platform administrator to:
//   1. See the currently configured database and storage provider
//   2. Select a different provider from the supported list
//   3. Test a connection string before applying it
//   4. Receive wrangler CLI instructions for applying the change
//
// No credentials are ever sent to or returned from the API.
// The connection string entered in this form is tested once, then discarded.

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  Database, HardDrive, Shield, CheckCircle, AlertTriangle,
  RefreshCw, Info, Server, Cloud, Cpu, Terminal, Eye, EyeOff,
} from 'lucide-react'

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'https://trafficprocure-api.dirlayo69.workers.dev'

type ProviderOption = {
  id: string
  label: string
  description: string
  icon: typeof Database
  location: 'cloud' | 'local' | 'hybrid'
  recommended?: boolean
  note?: string
}

const DB_PROVIDERS: ProviderOption[] = [
  { id: 'neon',     label: 'Neon PostgreSQL',         description: 'Managed serverless Postgres. Recommended for Cloudflare Workers deployment.', icon: Cloud,    location: 'cloud', recommended: true },
  { id: 'supabase', label: 'Supabase PostgreSQL',     description: 'Open-source Firebase alternative. Full Postgres with auth and storage.', icon: Cloud,    location: 'cloud' },
  { id: 'postgres', label: 'Self-hosted Postgres',    description: 'Your own Postgres instance on a VPS, AWS RDS, Azure, or on-premise server.', icon: Server,   location: 'hybrid' },
  { id: 'sqlite',   label: 'Local SQLite',             description: 'Offline-capable local database. No internet required. Best for air-gapped sites.', icon: HardDrive, location: 'local', note: 'Development / offline only — not suitable for multi-user production use.' },
  { id: 'custom',   label: 'Custom REST proxy',        description: 'Point to your own database proxy endpoint. MCB supplies the REST interface spec.', icon: Cpu,     location: 'hybrid' },
]

const STORAGE_PROVIDERS: ProviderOption[] = [
  { id: 'r2',    label: 'Cloudflare R2',      description: 'Zero-egress object storage. Included with Cloudflare Workers plan. Recommended.', icon: Cloud, location: 'cloud', recommended: true },
  { id: 's3',    label: 'AWS S3',             description: 'Industry-standard object storage. Works anywhere, costs per GB egress.', icon: Cloud, location: 'cloud' },
  { id: 'local', label: 'Local filesystem',   description: 'Store files on the same server as the API. Not suitable for Cloudflare Workers.', icon: HardDrive, location: 'local', note: 'Node.js deployments only — not compatible with Cloudflare Workers.' },
]

type HealthResult = {
  healthy: boolean
  provider?: string
  latencyMs?: number
  message?: string
  checkedAt?: string
}

type CurrentConfig = {
  provider?: string
  host?: string
  database?: string
  region?: string
  storageProvider?: string
  authProvider?: string
  environment?: string
  connectionConfigured?: boolean
}

function ProviderCard({ p, selected, onSelect }: { p: ProviderOption; selected: boolean; onSelect: () => void }) {
  const Icon = p.icon
  const locationColor = p.location === 'cloud' ? 'text-blue-600' : p.location === 'local' ? 'text-gray-500' : 'text-purple-600'
  const locationBg    = p.location === 'cloud' ? 'bg-blue-50'   : p.location === 'local' ? 'bg-gray-50'   : 'bg-purple-50'

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left border rounded-xl p-4 transition-all ${selected ? 'border-[#2E75B6] ring-2 ring-blue-100 bg-blue-50/30' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${selected ? 'bg-[#1F3864]' : 'bg-gray-100'}`}>
          <Icon className={`w-4 h-4 ${selected ? 'text-white' : 'text-gray-500'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className={`text-sm font-medium ${selected ? 'text-[#1F3864]' : 'text-gray-900'}`}>{p.label}</span>
            {p.recommended && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold">Recommended</span>}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${locationColor} ${locationBg}`}>
              {p.location === 'cloud' ? 'Cloud' : p.location === 'local' ? 'Local' : 'Self-hosted'}
            </span>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">{p.description}</p>
          {p.note && <p className="text-xs text-amber-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3 flex-shrink-0" />{p.note}</p>}
        </div>
        {selected && <CheckCircle className="w-4 h-4 text-[#2E75B6] flex-shrink-0 mt-0.5" />}
      </div>
    </button>
  )
}

export function DataSourceSettings() {
  const { isTier } = useAuthStore()
  const isAdmin = isTier('A_DEVELOPER')

  const [currentConfig, setCurrentConfig] = useState<CurrentConfig | null>(null)
  const [loading, setLoading]             = useState(false)
  const [loadingHealth, setLoadingHealth] = useState(false)
  const [healthResult, setHealthResult]   = useState<HealthResult | null>(null)
  const [testResult, setTestResult]       = useState<HealthResult | null>(null)
  const [selectedDb, setSelectedDb]       = useState('neon')
  const [selectedStorage, setSelectedStorage] = useState('r2')
  const [connString, setConnString]       = useState('')
  const [showConn, setShowConn]           = useState(false)
  const [instructions, setInstructions]   = useState<string[] | null>(null)
  const [testLoading, setTestLoading]     = useState(false)

  const loadCurrentConfig = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_BASE}/api/system/data-source`, {
        headers: { 'Content-Type': 'application/json' },
      })
      const data = await res.json()
      if (data.ok) {
        setCurrentConfig(data.dataSource)
        setSelectedDb(data.dataSource.provider ?? 'neon')
        setSelectedStorage(data.dataSource.storageProvider ?? 'r2')
      }
    } catch {
      setCurrentConfig({ provider: 'neon', storageProvider: 'r2', environment: 'development', connectionConfigured: false })
    }
    setLoading(false)
  }

  const checkHealth = async () => {
    setLoadingHealth(true)
    setHealthResult(null)
    try {
      const res = await fetch(`${API_BASE}/api/system/data-source/health`)
      const data = await res.json()
      setHealthResult(data.status)
    } catch {
      setHealthResult({ healthy: false, message: 'Could not reach API to run health check.', checkedAt: new Date().toISOString() })
    }
    setLoadingHealth(false)
  }

  const testAndApply = async () => {
    setTestLoading(true)
    setTestResult(null)
    setInstructions(null)
    try {
      const res  = await fetch(`${API_BASE}/api/system/data-source`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          provider:             selectedDb,
          storageProvider:      selectedStorage,
          testConnectionString: connString || undefined,
        }),
      })
      const data = await res.json()
      if (data.testResult) setTestResult(data.testResult)
      if (data.instructions) {
        const lines = [
          `wrangler secret put DATA_PROVIDER       # enter: ${data.instructions.DATA_PROVIDER ?? selectedDb}`,
          `wrangler secret put DATABASE_URL         # enter: <your connection string>`,
          `wrangler secret put STORAGE_PROVIDER     # enter: ${data.instructions.STORAGE_PROVIDER ?? selectedStorage}`,
          `wrangler deploy                          # redeploy with new secrets`,
        ]
        setInstructions(lines)
      }
    } catch {
      setTestResult({ healthy: false, message: 'API unreachable — check that the Worker is running.', checkedAt: new Date().toISOString() })
    }
    setTestLoading(false)
  }

  if (!isAdmin) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-6 text-center">
        <Shield className="w-8 h-8 text-amber-600 mx-auto mb-3" />
        <div className="font-semibold text-amber-900 mb-1">Administrator access required</div>
        <p className="text-sm text-amber-700">Data source configuration is restricted to Tier A (Developer / System Admin) only.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">

      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Database className="w-5 h-5 text-[#2E75B6]" />
          Data source & storage provider
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          TrafficProcure is provider-neutral. You can run the platform against any compatible database — cloud-hosted, self-hosted, or local.
          Changing the provider requires updating Wrangler secrets and redeploying the API.
        </p>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3 text-sm text-blue-800">
        <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <strong>No credentials leave this page.</strong> Connection strings entered here are tested once against the API health check and then discarded. They are never stored in the platform or returned by the API. The API always reads secrets from <code className="text-xs bg-blue-100 px-1 py-0.5 rounded">wrangler secret</code> — never from the frontend.
        </div>
      </div>

      {/* Current state */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-gray-900">Current configuration</h3>
          <div className="flex gap-2">
            <button onClick={loadCurrentConfig} disabled={loading} className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium flex items-center gap-1.5 transition-colors">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh
            </button>
            <button onClick={checkHealth} disabled={loadingHealth} className="text-xs px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg font-medium flex items-center gap-1.5 transition-colors">
              <CheckCircle className={`w-3.5 h-3.5 ${loadingHealth ? 'animate-pulse' : 'text-green-500'}`} />Test connection
            </button>
          </div>
        </div>

        {currentConfig ? (
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Database provider', value: currentConfig.provider ?? '—' },
              { label: 'Storage provider',  value: currentConfig.storageProvider ?? '—' },
              { label: 'Host',              value: currentConfig.host ?? 'not configured' },
              { label: 'Database name',     value: currentConfig.database ?? '—' },
              { label: 'Region',            value: currentConfig.region ?? '—' },
              { label: 'Environment',       value: currentConfig.environment ?? '—' },
              { label: 'Auth provider',     value: currentConfig.authProvider ?? '—' },
              { label: 'Connection configured', value: currentConfig.connectionConfigured ? 'Yes — DATABASE_URL secret set' : 'No — DATABASE_URL not set' },
            ].map(({ label, value }) => (
              <div key={label} className="bg-gray-50 rounded-lg px-3 py-2.5">
                <div className="text-xs text-gray-400 mb-0.5">{label}</div>
                <div className={`text-sm font-medium ${value.startsWith('No') || value === 'not configured' || value === '—' ? 'text-amber-600' : 'text-gray-900'}`}>{value}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-400 text-sm">
            Click Refresh to load the current configuration from the API.
          </div>
        )}

        {healthResult && (
          <div className={`mt-4 flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${healthResult.healthy ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {healthResult.healthy
              ? <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              : <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />}
            <div>
              <div className="font-medium">{healthResult.healthy ? 'Database reachable' : 'Connection failed'}</div>
              {healthResult.message && <div className="text-xs mt-0.5">{healthResult.message}</div>}
              {healthResult.latencyMs && <div className="text-xs mt-0.5">Latency: {healthResult.latencyMs}ms</div>}
            </div>
          </div>
        )}
      </div>

      {/* Database provider selection */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-medium text-gray-900 mb-1">Database provider</h3>
        <p className="text-xs text-gray-500 mb-4">Select where your data will be stored. All options use the same application code — only the connection changes.</p>
        <div className="space-y-2">
          {DB_PROVIDERS.map(p => (
            <ProviderCard key={p.id} p={p} selected={selectedDb === p.id} onSelect={() => setSelectedDb(p.id)} />
          ))}
        </div>
      </div>

      {/* Storage provider selection */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-medium text-gray-900 mb-1">File storage provider</h3>
        <p className="text-xs text-gray-500 mb-4">Where documents, photos, and archive packages are stored. Must be reachable from the API Worker.</p>
        <div className="space-y-2">
          {STORAGE_PROVIDERS.map(p => (
            <ProviderCard key={p.id} p={p} selected={selectedStorage === p.id} onSelect={() => setSelectedStorage(p.id)} />
          ))}
        </div>
      </div>

      {/* Connection string test */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-medium text-gray-900 mb-1">Test connection string</h3>
        <p className="text-xs text-gray-500 mb-4">
          Optionally enter a connection string to test it before applying. This string is sent to the API once for a health check and is not stored.
          Leave blank to validate only the provider selection.
        </p>
        <div className="relative mb-4">
          <input
            type={showConn ? 'text' : 'password'}
            value={connString}
            onChange={e => setConnString(e.target.value)}
            placeholder={`postgresql://user:password@host/database${selectedDb === 'neon' ? '?sslmode=require' : ''}`}
            className="input text-sm font-mono pr-10 w-full"
          />
          <button onClick={() => setShowConn(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            {showConn ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        <button onClick={testAndApply} disabled={testLoading} className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
          {testLoading
            ? <><RefreshCw className="w-4 h-4 animate-spin" />Testing…</>
            : <><CheckCircle className="w-4 h-4" />Validate and generate apply instructions</>}
        </button>

        {/* Test result */}
        {testResult && (
          <div className={`mt-4 flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${testResult.healthy ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {testResult.healthy
              ? <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              : <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />}
            <div>
              <div className="font-medium">{testResult.healthy ? 'Connection string valid' : 'Connection failed'}</div>
              {testResult.message && <div className="text-xs mt-0.5">{testResult.message}</div>}
              {testResult.latencyMs && <div className="text-xs mt-0.5 text-green-700">Latency: {testResult.latencyMs}ms</div>}
            </div>
          </div>
        )}

        {/* wrangler CLI instructions */}
        {instructions && (
          <div className="mt-4 bg-gray-900 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Terminal className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-400 font-medium">Run these commands to apply the new provider</span>
            </div>
            <div className="space-y-1.5">
              {instructions.map((line, i) => (
                <div key={i} className="text-xs font-mono text-green-400 leading-relaxed">{line}</div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-gray-700 text-[11px] text-gray-500">
              Secrets are stored in Cloudflare Workers — they never appear in source code, ZIP handovers, or this settings page. The Worker automatically reloads after deploy.
            </div>
          </div>
        )}
      </div>

      {/* Architecture note */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
        <h3 className="font-medium text-gray-900 mb-2 text-sm flex items-center gap-2">
          <Info className="w-4 h-4 text-gray-500" />
          How the provider adapter works
        </h3>
        <div className="text-xs text-gray-600 space-y-2 leading-relaxed">
          <p>TrafficProcure uses a <strong>provider adapter</strong> pattern. All database operations in the API go through a stable <code className="bg-gray-200 px-1 rounded">IDatabaseAdapter</code> interface. The platform ships with adapters for Neon, Supabase, self-hosted Postgres, local SQLite, and a custom REST proxy.</p>
          <p>When you set <code className="bg-gray-200 px-1 rounded">DATA_PROVIDER=supabase</code> (for example) in your Wrangler secrets and redeploy, the API creates a <code className="bg-gray-200 px-1 rounded">SupabaseAdapter</code> automatically. No application code changes are needed.</p>
          <p>The same pattern applies to object storage (<code className="bg-gray-200 px-1 rounded">IObjectStorageAdapter</code>: R2, S3, or local) and authentication (<code className="bg-gray-200 px-1 rounded">IAuthAdapter</code>: Clerk or custom JWT).</p>
          <p className="text-gray-500">Adapter source code: <code className="bg-gray-200 px-1 rounded">prototype/apps/api/src/adapters/</code></p>
        </div>
      </div>

    </div>
  )
}
